/**
 * GitHubTab - Thin orchestrator for the GitHub tab.
 *
 * Composes the new sub-components:
 *   - ConnectedRepos: list of connected repos (add/remove)
 *   - DriftInbox: open + resolved drift findings
 *   - RecentCommits: commit history with drift markers
 *
 * Preserves:
 *   - canUseGitHub() paywall
 *   - GitHub App install flow (/github/status, /github/app/install-url)
 *   - Existing RepositorySelector for picking a repo to add
 *
 * Props: { ideaId, idea, onRefreshIdea, onShowUpgrade }
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Text, Heading3, LoadingSpinner } from '../design-system';
import {
  useAddRepo,
  useRemoveRepo,
  useDriftState,
  useTriggerDriftScan,
  usePreviewUpdateSpec,
  useUpdateSpecFromFinding,
  useDismissFinding,
  useUndismissFinding,
  useRecentCommits,
} from '../hooks/api/useIdeas';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useToast } from '../hooks/useToast';
import apiClient from '../services/unifiedApiClient';
import RepositorySelector from './RepositorySelector';

import ConnectedRepos from './github/ConnectedRepos';
import DriftInbox from './github/DriftInbox';
import RecentCommits from './github/RecentCommits';
import UpdateSpecResultModal from './github/UpdateSpecResultModal';

const T = {
  hand: '"Caveat", "Nunito", cursive',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  orange: '#EA7C1F',
  orangeDark: '#C25F0E',
};

function TabHeader() {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontFamily: T.hand, fontSize: 22, color: T.orange, lineHeight: 1 }}>
        code link —
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 6px' }}>
        <h1 style={{
          fontFamily: T.sans, fontSize: 30, fontWeight: 800,
          color: T.ink, letterSpacing: -0.6, margin: 0,
        }}>
          GitHub
        </h1>
        <span style={{
          fontFamily: T.sans, fontSize: 11, fontWeight: 800,
          letterSpacing: '0.6px', textTransform: 'uppercase',
          color: T.orangeDark,
          backgroundColor: 'rgba(234,124,31,0.18)',
          border: '1px solid rgba(234,124,31,0.3)',
          padding: '3px 10px', borderRadius: 999, lineHeight: 1,
        }}>
          Pro
        </span>
      </div>
      <p style={{ fontFamily: T.sans, fontSize: 14, color: T.ink2, margin: 0 }}>
        Wire this idea to a repo and Cahoots will watch for drift between the plan and what ships.
      </p>
    </div>
  );
}

const GitHubTab = ({ ideaId, idea, onRefreshIdea, onShowUpgrade }) => {
  const { canUseGitHub } = useSubscription();
  const { showToast, ToastComponent } = useToast();

  // ---- GitHub App connection state (preserved from old impl) ----
  const [githubConnected, setGithubConnected] = useState(null); // null=loading
  const [installingApp, setInstallingApp] = useState(false);
  const [installError, setInstallError] = useState(null);
  // Finding IDs the user has already "Update spec"'d this session — filtered out
  // of the open inbox until the next scan refreshes drift state.
  const [appliedFindingIds, setAppliedFindingIds] = useState(() => new Set());
  // Two-step Update spec flow: clicking the button fetches a preview, opens
  // the modal; user reviews + selects cascades, then clicks Apply.
  const [pendingFindingId, setPendingFindingId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (!canUseGitHub()) return;
    apiClient
      .get('/github/status')
      .then((res) => setGithubConnected(res.connected || false))
      .catch(() => setGithubConnected(false));
  }, [canUseGitHub]);

  const handleInstallApp = async () => {
    setInstallingApp(true);
    setInstallError(null);
    try {
      const returnTo = window.location.pathname + window.location.search;
      const data = await apiClient.get('/github/app/install-url', {
        params: { return_to: returnTo },
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setInstallError('GitHub App is not configured.');
    } catch (e) {
      setInstallError(e?.message || 'Could not load install URL.');
    }
    setInstallingApp(false);
  };

  // ---- Repo management ----
  const addRepoMutation = useAddRepo();
  const removeRepoMutation = useRemoveRepo();
  const [showRepoSelector, setShowRepoSelector] = useState(false);

  // ---- Persisted scan state (survives tab navigation) ----
  const SCAN_KEY = ideaId ? `drift_scan_${ideaId}` : null;
  const SCAN_TIMEOUT_MS = 3 * 60 * 1000; // 3 min max

  const getScanStartedAt = () => {
    if (!SCAN_KEY) return null;
    const val = sessionStorage.getItem(SCAN_KEY);
    if (!val) return null;
    const ts = parseInt(val, 10);
    if (Date.now() - ts > SCAN_TIMEOUT_MS) {
      sessionStorage.removeItem(SCAN_KEY);
      return null;
    }
    return ts;
  };

  const [scanStartedAt, setScanStartedAt] = useState(() => getScanStartedAt());
  const persistedScanning = scanStartedAt !== null;

  // ---- Drift state (new shape) ----
  // Poll every 5 s while a scan is in progress so we detect completion.
  const scanMutation = useTriggerDriftScan();
  const isScanning = scanMutation.isPending || persistedScanning;

  const { data: driftData, isLoading } = useDriftState(ideaId, {
    enabled: !!ideaId && canUseGitHub(),
    refetchInterval: isScanning ? 5000 : false,
  });

  // When drift state refreshes with any repo scanned after our start time,
  // the scan completed — clear the persisted flag.
  useEffect(() => {
    if (!persistedScanning || !driftData?.repos) return;
    const latestScan = Object.values(driftData.repos)
      .map(r => r?.last_scanned_at ? new Date(r.last_scanned_at).getTime() : 0)
      .reduce((max, t) => Math.max(max, t), 0);
    if (latestScan >= scanStartedAt) {
      sessionStorage.removeItem(SCAN_KEY);
      setScanStartedAt(null);
    }
  }, [driftData, persistedScanning, scanStartedAt, SCAN_KEY]);
  const previewMutation = usePreviewUpdateSpec();
  const updateSpecMutation = useUpdateSpecFromFinding();
  const dismissMutation = useDismissFinding(ideaId);
  const undismissMutation = useUndismissFinding(ideaId);

  const state = driftData || {
    findings: [],
    previous_findings: [],
    repos: {},
    github_repos: [],
  };
  // Prefer drift state's repo list when present; fall back to idea-level list.
  const githubRepos = state.github_repos?.length
    ? state.github_repos
    : idea?.github_repos || [];

  const { data: commits = [] } = useRecentCommits(githubRepos);

  const driftCommitShas = useMemo(
    () =>
      new Set(
        (state.findings || [])
          .map((f) => f.commit_sha)
          .filter(Boolean),
      ),
    [state.findings],
  );

  // ---- WebSocket: refresh on drift scan completion ----
  useEffect(() => {
    const handler = (event) => {
      const data = event.detail || {};
      if (data.idea_id === ideaId) {
        if (onRefreshIdea) onRefreshIdea();
      }
    };
    window.addEventListener('drift.scan_complete', handler);
    return () => window.removeEventListener('drift.scan_complete', handler);
  }, [ideaId, onRefreshIdea]);

  // ---- Paywall gate ----
  if (!canUseGitHub()) {
    return (
      <div>
        <TabHeader />
        <Card>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Heading3>Connect your code</Heading3>
            <Text style={{ marginTop: 8, marginBottom: 16 }}>
              Connect your repositories to detect what's already built, track
              drift, and keep your spec in sync with your codebase.
            </Text>
            <Button variant="primary" onClick={onShowUpgrade}>
              Upgrade to unlock
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ---- GitHub App not installed yet ----
  if (githubConnected === false) {
    return (
      <div>
        <TabHeader />
        <Card>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Heading3>Install the Cahoots GitHub App</Heading3>
            <Text style={{ marginTop: 8, marginBottom: 16 }}>
              Install the GitHub App so we can read your repositories and scan
              for spec drift.
            </Text>
            <Button
              variant="primary"
              onClick={handleInstallApp}
              disabled={installingApp}
            >
              {installingApp ? 'Redirecting…' : 'Install GitHub App'}
            </Button>
            {installError && (
              <Text style={{ marginTop: 12, color: 'var(--color-error, #C14A33)' }}>
                {installError}
              </Text>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // ---- Loading ----
  if (githubConnected === null || isLoading) {
    return (
      <div>
        <TabHeader />
        <div style={{ padding: 32, textAlign: 'center' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // ---- Handlers ----
  const handleScan = () => {
    if (!ideaId || isScanning) return;
    setAppliedFindingIds(new Set());
    const startedAt = Date.now();
    sessionStorage.setItem(SCAN_KEY, String(startedAt));
    setScanStartedAt(startedAt);
    scanMutation.mutate({ ideaId }, {
      onSettled: () => {
        // Mutation completed (success or error) — clear persisted flag.
        sessionStorage.removeItem(SCAN_KEY);
        setScanStartedAt(null);
      },
    });
  };

  // Step 1: user clicks "Update spec" on a finding row → fetch the preview
  // (spec changes + LLM-suggested cascades) and open the modal.
  const handleUpdateSpec = (findingId) => {
    setPendingFindingId(findingId);
    setPreviewData(null);
    setPreviewError(null);
    previewMutation.mutate(
      { ideaId, findingId },
      {
        onSuccess: (data) => setPreviewData(data),
        onError: (e) => setPreviewError(e),
      }
    );
  };

  // Step 2: user reviewed in the modal and clicked Apply.
  const handleApplyUpdateSpec = (approvedCascades) => {
    if (!pendingFindingId) return;
    const findingId = pendingFindingId;
    updateSpecMutation.mutate(
      { ideaId, findingId, approvedCascades },
      {
        onSuccess: (data) => {
          setAppliedFindingIds((prev) => {
            const next = new Set(prev);
            next.add(findingId);
            return next;
          });
          const applied = data?.applied_cascade_count ?? 0;
          showToast(
            applied > 0
              ? `Spec updated · ${applied} cascade${applied === 1 ? '' : 's'} applied`
              : 'Spec updated',
            'success'
          );
          // Close the modal
          setPendingFindingId(null);
          setPreviewData(null);
          setPreviewError(null);
        },
        onError: (e) => {
          const detail =
            e?.response?.data?.detail || e?.message || 'Could not update spec.';
          showToast(detail, 'error');
        },
      }
    );
  };

  const handleCloseUpdateSpec = () => {
    if (updateSpecMutation.isPending) return; // don't dismiss mid-apply
    setPendingFindingId(null);
    setPreviewData(null);
    setPreviewError(null);
  };

  const handleDismissFinding = (findingId) => {
    dismissMutation.mutate(
      { findingId },
      {
        onSuccess: () => showToast('Finding dismissed', 'success'),
        onError: (e) => showToast(
          e?.response?.data?.detail || e?.message || 'Could not dismiss finding.',
          'error',
        ),
      }
    );
  };

  const handleUndismissFinding = (signature) => {
    undismissMutation.mutate(
      { signature },
      {
        onSuccess: () => showToast('Finding restored', 'success'),
        onError: (e) => showToast(
          e?.response?.data?.detail || e?.message || 'Could not restore finding.',
          'error',
        ),
      }
    );
  };

  const handleAddRepo = () => setShowRepoSelector(true);

  // RepositorySelector calls onChange with { type, url, branch, name } or null.
  // The mutation expects { ideaId, url, defaultBranch }.
  const handleSelectRepoFromPicker = async (repo) => {
    if (!repo) return;
    try {
      await addRepoMutation.mutateAsync({
        ideaId,
        url: repo.url,
        defaultBranch: repo.branch || 'main',
      });
      onRefreshIdea?.();
      setShowRepoSelector(false);
    } catch (e) {
      console.error('Failed to add repo:', e);
    }
  };

  const handleDisconnectRepo = async (repo) => {
    if (!window.confirm(`Disconnect ${repo.owner}/${repo.repo}?`)) return;
    try {
      await removeRepoMutation.mutateAsync({ ideaId, url: repo.url });
      onRefreshIdea?.();
    } catch (e) {
      console.error('Failed to remove repo:', e);
    }
  };

  // ---- Render ----
  return (
    <div>
      <TabHeader />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConnectedRepos
        repos={githubRepos}
        repoMeta={state.repos || {}}
        onDisconnect={handleDisconnectRepo}
        onAdd={handleAddRepo}
      />

      {showRepoSelector && (
        <div style={{
          background: '#FFFBEC',
          border: '1px solid #D7C9A1',
          borderRadius: 14,
          padding: 16,
          overflow: 'visible',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}>
            <span style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 700, color: T.ink }}>
              Add a repository
            </span>
            <button
              onClick={() => setShowRepoSelector(false)}
              style={{
                fontFamily: T.sans,
                fontSize: 13,
                fontWeight: 600,
                color: T.inkMuted,
                background: 'none',
                border: '1px dashed #D7C9A1',
                borderRadius: 6,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          <RepositorySelector
            value={null}
            onChange={handleSelectRepoFromPicker}
          />
          {addRepoMutation.error && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#C14A33', fontFamily: T.sans }}>
              Could not add repository: {addRepoMutation.error?.message || 'unknown error'}
            </div>
          )}
        </div>
      )}

      {removeRepoMutation.error && (
        <Card>
          <div style={{ padding: 12 }}>
            <Text style={{ color: 'var(--color-error, #C14A33)' }}>
              Could not disconnect repository: {removeRepoMutation.error?.message || 'unknown error'}
            </Text>
          </div>
        </Card>
      )}


      {githubRepos.length > 0 && (
        <DriftInbox
          findings={(state.findings || []).filter((f) => !appliedFindingIds.has(f.id))}
          previousFindings={[
            ...(state.findings || []).filter((f) => appliedFindingIds.has(f.id)),
            ...(state.previous_findings || []),
          ]}
          dismissed={state.dismissed || []}
          repoMeta={state.repos || {}}
          githubRepos={githubRepos}
          onUpdateSpec={handleUpdateSpec}
          updateSpecRunning={updateSpecMutation.isPending}
          onDismiss={handleDismissFinding}
          onUndismiss={handleUndismissFinding}
          dismissRunning={dismissMutation.isPending}
          onScan={handleScan}
          scanning={isScanning}
        />
      )}

      <RecentCommits
        commits={commits}
        multiRepo={githubRepos.length >= 2}
        driftCommitShas={driftCommitShas}
      />
      </div>
      <ToastComponent />
      <UpdateSpecResultModal
        preview={previewData}
        loading={previewMutation.isPending && !previewData}
        applying={updateSpecMutation.isPending}
        error={previewError}
        onApply={handleApplyUpdateSpec}
        onClose={handleCloseUpdateSpec}
        onDismiss={(findingId) => {
          dismissMutation.mutate(
            { findingId },
            {
              onSuccess: () => {
                showToast('Finding dismissed', 'success');
                handleCloseUpdateSpec();
              },
              onError: (e) => showToast(
                e?.response?.data?.detail || e?.message || 'Could not dismiss finding.',
                'error',
              ),
            }
          );
        }}
        dismissing={dismissMutation.isPending}
      />
    </div>
  );
};

export default GitHubTab;
